import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import {
    Paper,
    Avatar,
    Typography,
    Box,
    Chip,
    styled,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import { blue } from '@mui/material/colors';

import {
    statusFilter, summaryFilter, fleetSummarySelector, assetListSelector,
    setViewType, getAddress, getKilometersOneAsset,
    visibleAssetsListSelctor, pinnedAssetsSelector,
    selectAllVehicles
} from '../../features/assets/assetsSlice';
import { liveTrackinglayoutSelector } from '../../features/layout/layoutSlice';

import AssetViewControl from '../../components/assetView/AssetViewControl';
import { periodOptionsInline } from '../../utils/datePeriodCalculator';
import { clearVisibleAssetsMarkers, restoreVisibleAssetsMarkers } from '../../features/assets/assetsSlice';
import { setMapControl, enableMapToolbar, disableMapToolbar, toggleVehicleIcons } from '../../features/map/mapSlice';



const StatusChip = styled(Chip)(({ theme, colorbg, colorfg, colortext }) => ({
    margin: theme.spacing(0.25),
    height: 22,
    fontSize: 12,
    backgroundColor: colorbg,
    color: colorfg,
    border: colortext ? `1px solid ${colortext}` : undefined,
    fontWeight: 500,
    minWidth: 60,
}));

function checkDistancePeriod(period) {
    let val = false;
    periodOptionsInline.forEach((element) => {
        if (element.value === period) {
            val = element.name;
        }
    });
    return val || false;
}

// Signal bar component
const SignalBars = ({ value = 0, max = 5, color = '#4caf50' }) => (
    <Box display="flex" alignItems="center" sx={{ height: 16 }}>
        {[...Array(max)].map((_, i) => (
            <Box
                key={i}
                sx={{
                    width: 3,
                    height: 6 + i * 2,
                    mx: 0.2,
                    borderRadius: 0.5,
                    backgroundColor: i < value ? color : '#e0e0e0',
                    transition: 'background 0.2s',
                }}
            />
        ))}
    </Box>
);

const MyFleet = () => {
    const theme = useTheme();
    const dimentions = useSelector(liveTrackinglayoutSelector);
    const dispatch = useDispatch();
    const [reload, setReload] = useState(true);

    const fleetSummary = useSelector(fleetSummarySelector);
    const allVehicles = useSelector(selectAllVehicles);
    const vehicleEntities = allVehicles.reduce((acc, vehicle) => {
        acc[vehicle.vehicleDevice.assetId] = vehicle;
        return acc;
    }, {});
    //console.log("Vehicle entities", fleetSummary);
    const assetList = useSelector(assetListSelector);
    const visibleAssetsList = useSelector(visibleAssetsListSelctor);

    const loadAddrDist = () => {
        if (reload) {
            visibleAssetsList.forEach(assetId => {
                const assetData = vehicleEntities[assetId];
                const address = assetData && assetData.address;
                if (assetData && assetData.coreData && !address) {
                    //console.log("Lets get address");
                    dispatch(getAddress({
                        assetId,
                        lat: assetData.coreData.lat,
                        lng: assetData.coreData.lng,
                    }))
                }
                const distanceMatrix = assetData && assetData.distanceMatrix;
                if (typeof (distanceMatrix) === 'string') {
                    //console.log("Lets get distanceMatrix", typeof (distanceMatrix), distanceOpen);
                    dispatch(getKilometersOneAsset({
                        assetId
                    }));
                }
            })
            setReload(false);
            console.log("Distance and addr load done");
        }
    }

    useEffect(() => {
        dispatch(clearVisibleAssetsMarkers());
        dispatch(toggleVehicleIcons(false));
        dispatch(disableMapToolbar());
        return () => {
            dispatch(setMapControl('asset'));
            dispatch(restoreVisibleAssetsMarkers());
            dispatch(toggleVehicleIcons(true));
            dispatch(enableMapToolbar());
        }
    }, [dispatch]);


    useEffect(() => {
        if (reload) {
            loadAddrDist();
        }

        const intervalId = setInterval(() => {
            setReload(true);
            loadAddrDist();
        }, 5 * 60 * 1000);

        return () => clearInterval(intervalId);
    }, [dispatch]);// eslint-disable-line react-hooks/exhaustive-deps


    const buildTableRows = () => {
        // Get allowed distance keys from periodOptionsInline
        const allowedDistanceKeys = periodOptionsInline.map(x => x.value);
        return visibleAssetsList.map((assetId, rowIdx) => {
            const assetData = vehicleEntities[assetId];
            if (!assetData) return null;
            const { coreData, sensorData, address, vehicleDevice, statusMeta, dataSanity } = assetData;
            const sensors = sensorData ? sensorData.Sensors : null;
            const distanceMatrix = assetData && assetData.distanceMatrix;
            const dt_tracker = coreData && coreData.dt_tracker ? moment(coreData.dt_tracker).format('D MMM YY, h:mm:ss a') : '';
            const signal_gps = coreData && typeof coreData.signal_gps === 'number' ? coreData.signal_gps : 0;
            const signal_gsm = coreData && typeof coreData.signal_gsm === 'number' ? coreData.signal_gsm : 0;
            const statusLabel = (
                <StatusChip
                    label={statusMeta.status}
                    colorbg={statusMeta.colorbg}
                    colorfg={statusMeta.colorfg}
                    colortext={statusMeta.colortext}
                    size="small"
                    sx={{ fontSize: 11, height: 18, minWidth: 32, p: 0, mt: 0.5 }}
                />
            );
            // Sensors: omit 'Address' and any sensor whose name contains both 'temp' and 'id'
            let sensorEntries = [];
            if (sensors) {
                sensorEntries = Object.keys(sensors)
                    .filter(name => {
                        const lower = name.toLowerCase();
                        if (lower.includes('address')) return false;
                        return !(lower.includes('temp') && lower.includes('id'));
                    })
                    .sort((a, b) => a.localeCompare(b))
                    .map(name => ({ name, value: sensors[name] }));
            }
            // Distance: only allowed keys
            let filteredDistances = [];
            if (distanceMatrix) {
                filteredDistances = allowedDistanceKeys
                    .filter(key => distanceMatrix[key] !== undefined)
                    .map(key => ({
                        key,
                        label: checkDistancePeriod(key) || key,
                        value: Math.round(Number(distanceMatrix[key]))
                    }));
            }
            return (
                <TableRow
                    key={assetId}
                    hover
                    sx={{
                        backgroundColor: rowIdx % 2 === 0 ? theme.palette.action.hover : 'inherit',
                        transition: 'background 0.2s',
                        '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                        },
                    }}
                >
                    <TableCell sx={{ minWidth: 180, borderBottom: '1px solid #eee', p: 1 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Avatar style={{ backgroundColor: 'white' }}>
                                <img alt="a" src={`/assets/markers/def30/${vehicleDevice.icon}`} />
                            </Avatar>
                            <Box flex={1} minWidth={0}>
                                <Typography variant="body2" fontWeight={600} noWrap>{vehicleDevice.regn}</Typography>
                                {statusLabel}
                            </Box>
                        </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, borderBottom: '1px solid #eee', p: 1 }}>{dt_tracker}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #eee', p: 1 }}><SignalBars value={signal_gps} color={theme.palette.primary.main} /></TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #eee', p: 1 }}><SignalBars value={signal_gsm} color={theme.palette.secondary.main} /></TableCell>
                    <TableCell sx={{ fontSize: 12, borderBottom: '1px solid #eee', p: 1, minWidth: 220 }}>
                        {sensorEntries.length > 0 && (
                            <Box>
                                {(() => {
                                    const isLong = (entry) => `${entry.name}: ${entry.value}`.length > 16;
                                    const rows = [];
                                    let i = 0;
                                    while (i < sensorEntries.length) {
                                        const entry = sensorEntries[i];
                                        if (isLong(entry)) {
                                            rows.push([
                                                <Box
                                                    key={entry.name}
                                                    display="inline-flex"
                                                    alignItems="center"
                                                    sx={{
                                                        backgroundColor: theme.palette.grey[100],
                                                        borderRadius: 2,
                                                        boxShadow: 1,
                                                        px: 1.2,
                                                        py: 0.7,
                                                        mb: 1,
                                                        fontSize: 13,
                                                        minWidth: 0,
                                                        maxWidth: '100%',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 }}>{entry.name}:</Typography>
                                                    <Typography variant="body2" sx={{ fontSize: 13, ml: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 }}>{entry.value}</Typography>
                                                </Box>
                                            ]);
                                            i++;
                                        } else {
                                            const next = sensorEntries[i + 1];
                                            if (next && !isLong(next)) {
                                                rows.push([
                                                    <Box
                                                        key={entry.name}
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        sx={{
                                                            backgroundColor: theme.palette.grey[100],
                                                            borderRadius: 2,
                                                            boxShadow: 1,
                                                            px: 1.2,
                                                            py: 0.7,
                                                            mb: 1,
                                                            mr: 1.5,
                                                            fontSize: 13,
                                                            minWidth: 0,
                                                            maxWidth: '48%',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>{entry.name}:</Typography>
                                                        <Typography variant="body2" sx={{ fontSize: 13, ml: 0.5 }}>{entry.value}</Typography>
                                                    </Box>,
                                                    <Box
                                                        key={next.name}
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        sx={{
                                                            backgroundColor: theme.palette.grey[100],
                                                            borderRadius: 2,
                                                            boxShadow: 1,
                                                            px: 1.2,
                                                            py: 0.7,
                                                            mb: 1,
                                                            fontSize: 13,
                                                            minWidth: 0,
                                                            maxWidth: '48%',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>{next.name}:</Typography>
                                                        <Typography variant="body2" sx={{ fontSize: 13, ml: 0.5 }}>{next.value}</Typography>
                                                    </Box>
                                                ]);
                                                i += 2;
                                            } else {
                                                rows.push([
                                                    <Box
                                                        key={entry.name}
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        sx={{
                                                            backgroundColor: theme.palette.grey[100],
                                                            borderRadius: 2,
                                                            boxShadow: 1,
                                                            px: 1.2,
                                                            py: 0.7,
                                                            mb: 1,
                                                            fontSize: 13,
                                                            minWidth: 0,
                                                            maxWidth: '100%',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 }}>{entry.name}:</Typography>
                                                        <Typography variant="body2" sx={{ fontSize: 13, ml: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 }}>{entry.value}</Typography>
                                                    </Box>
                                                ]);
                                                i++;
                                            }
                                        }
                                    }
                                    return rows.map((row, idx) => (
                                        <Box key={idx} display="flex" flexDirection="row" alignItems="center" mb={0.5}>
                                            {row}
                                        </Box>
                                    ));
                                })()}
                            </Box>
                        )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #eee', p: 1 }}>
                        {filteredDistances.length > 0 && (
                            <Box>
                                {(() => {
                                    const isLong = (d) => `${d.label}: ${d.value}`.length > 16;
                                    const rows = [];
                                    let i = 0;
                                    while (i < filteredDistances.length) {
                                        const d = filteredDistances[i];
                                        if (isLong(d)) {
                                            rows.push([
                                                <Box
                                                    key={d.key}
                                                    display="inline-flex"
                                                    alignItems="center"
                                                    sx={{
                                                        backgroundColor: theme.palette.grey[200],
                                                        borderRadius: 2,
                                                        boxShadow: 1,
                                                        px: 1.2,
                                                        py: 0.7,
                                                        mb: 1,
                                                        fontSize: 13,
                                                        minWidth: 0,
                                                        maxWidth: '98%',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>{d.label}:</Typography>
                                                    <Typography variant="body2" sx={{ fontSize: 13, ml: 0.5 }}>{d.value}</Typography>
                                                </Box>
                                            ]);
                                            i++;
                                        } else {
                                            const next = filteredDistances[i + 1];
                                            if (next && !isLong(next)) {
                                                rows.push([
                                                    <Box
                                                        key={d.key}
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        sx={{
                                                            backgroundColor: theme.palette.grey[200],
                                                            borderRadius: 2,
                                                            boxShadow: 1,
                                                            px: 1.2,
                                                            py: 0.7,
                                                            mb: 1,
                                                            mr: 1.5,
                                                            fontSize: 13,
                                                            minWidth: 0,
                                                            maxWidth: '48%',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>{d.label}:</Typography>
                                                        <Typography variant="body2" sx={{ fontSize: 13, ml: 0.5 }}>{d.value}</Typography>
                                                    </Box>,
                                                    <Box
                                                        key={next.key}
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        sx={{
                                                            backgroundColor: theme.palette.grey[200],
                                                            borderRadius: 2,
                                                            boxShadow: 1,
                                                            px: 1.2,
                                                            py: 0.7,
                                                            mb: 1,
                                                            fontSize: 13,
                                                            minWidth: 0,
                                                            maxWidth: '48%',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>{next.label}:</Typography>
                                                        <Typography variant="body2" sx={{ fontSize: 13, ml: 0.5 }}>{next.value}</Typography>
                                                    </Box>
                                                ]);
                                                i += 2;
                                            } else {
                                                rows.push([
                                                    <Box
                                                        key={d.key}
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        sx={{
                                                            backgroundColor: theme.palette.grey[200],
                                                            borderRadius: 2,
                                                            boxShadow: 1,
                                                            px: 1.2,
                                                            py: 0.7,
                                                            mb: 1,
                                                            fontSize: 13,
                                                            minWidth: 0,
                                                            maxWidth: '98%',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>{d.label}:</Typography>
                                                        <Typography variant="body2" sx={{ fontSize: 13, ml: 0.5 }}>{d.value}</Typography>
                                                    </Box>
                                                ]);
                                                i++;
                                            }
                                        }
                                    }
                                    return rows.map((row, idx) => (
                                        <Box key={idx} display="flex" flexDirection="row" alignItems="center" mb={0.5}>
                                            {row}
                                        </Box>
                                    ));
                                })()}
                            </Box>
                        )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 16, borderBottom: '1px solid #eee', p: 1, minWidth: 180 }}>
                        <Box>
                            {address && (
                                <>
                                    {address.Combined && (
                                        <Typography variant="body1" color="text.primary" sx={{ fontSize: 16, fontWeight: 600 }}>
                                            {address.Combined}
                                        </Typography>
                                    )}
                                    {address.Location && address.Combined !== address.Location && (
                                        <Typography variant="body2" color="text.primary" sx={{ fontSize: 15, fontWeight: 500 }}>
                                            {address.Location}
                                        </Typography>
                                    )}
                                    {address.Landmark && (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                                            {address.Landmark}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>
                    </TableCell>
                </TableRow>
            );
        });
    };

    const runStatusFiler = (type) => {
        dispatch(setViewType('filteredList'));
        dispatch(statusFilter(type));
        /*if (type !== 'No Data') {
            dispatch(statusFilter(type));
        }*/

    }
    const runSummaryFilter = (type) => {
        dispatch(setViewType('filteredList'));
        dispatch(summaryFilter(type));
    }
    return (
        <Box>
            <Box style={{ ...dimentions.uppersidebar }}>
                <AssetViewControl />
            </Box>

            <Box style={{ ...dimentions.bodyHeader, overflow: 'hidden', padding: 0, marginBottom: 4 }}>
                <Box display="flex" width="100%" gap={1} alignItems="stretch" height="100%" style={{ marginTop: 4 }}>
                    {/* Fleet Size Box */}
                    <Box flex={1} display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" height="100%" p={2} borderRadius={2} boxShadow={1} bgcolor="#fff">
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, fontSize: 18, ml: 1, cursor: 'pointer' }}
                            onClick={() => runSummaryFilter('Fleet Size')}
                        >{'Fleet Size'}</Typography>
                        <Avatar
                            sx={{ bgcolor: blue[500], width: 40, height: 40, fontSize: 22, mr: 1, cursor: 'pointer' }}
                            onClick={() => runSummaryFilter('Fleet Size')}
                        >{assetList.length}</Avatar>
                    </Box>
                    {/* Status Summary Boxes */}
                    {Object.keys(fleetSummary).map((summaryStatus) => {
                        const statusKeys = Object.keys(fleetSummary[summaryStatus].status);
                        const summaryColor = statusKeys.length > 0 ? fleetSummary[summaryStatus].status[statusKeys[0]].color : fleetSummary[summaryStatus].color;
                        return (
                            <Box key={summaryStatus} flex={1} display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" height="100%" p={2} borderRadius={2} boxShadow={1} bgcolor="#fff">
                                {/* Left: Summary name and status items */}
                                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="flex-start" height="100%" width="100%">
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700, fontSize: 18, color: summaryColor, mb: 0.2, cursor: 'pointer' }}
                                        onClick={() => runSummaryFilter(summaryStatus)}
                                    >{summaryStatus}</Typography>
                                    <Box display="flex" flexWrap="wrap" gap={0.3} mt={0.2}>
                                        {statusKeys.slice(0, 4).map((status) => (
                                            <Box key={status} display="flex" alignItems="baseline" gap={0.2} sx={{ mr: 0.7 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ fontWeight: 600, fontSize: 10.5, color: fleetSummary[summaryStatus].status[status].color, cursor: 'pointer' }}
                                                    onClick={() => runStatusFiler(status)}
                                                >{status}</Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        backgroundColor: fleetSummary[summaryStatus].status[status].colorbg,
                                                        color: fleetSummary[summaryStatus].status[status].colorfg,
                                                        borderRadius: 1,
                                                        px: 0.6,
                                                        fontWeight: 700,
                                                        fontSize: 10.5,
                                                        minWidth: 16,
                                                        textAlign: 'center',
                                                        ml: 0.2,
                                                        cursor: 'pointer',
                                                        display: 'inline-block',
                                                    }}
                                                    onClick={() => runStatusFiler(status)}
                                                >{fleetSummary[summaryStatus].status[status].count}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                                {/* Right: Summary count in Avatar */}
                                <Avatar
                                    sx={{ bgcolor: summaryColor, width: 40, height: 40, fontSize: 22, ml: 2, alignSelf: 'center', cursor: 'pointer' }}
                                    onClick={() => runSummaryFilter(summaryStatus)}
                                >{fleetSummary[summaryStatus].count}</Avatar>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Box sx={{ ...dimentions.bodyTable, overflow: 'auto', maxHeight: 'calc(100vh - 180px)', mt: 0 }}>
                <TableContainer component={Paper} sx={{ boxShadow: 1, borderRadius: 2, minWidth: 900 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Vehicle</TableCell>
                                <TableCell>Last Update</TableCell>
                                <TableCell>GPS</TableCell>
                                <TableCell>GSM</TableCell>
                                <TableCell>Sensors</TableCell>
                                <TableCell>Distance</TableCell>
                                <TableCell>Location</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {buildTableRows()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default React.memo(MyFleet);


/*
One screen snaphot of all vehicles showing location, status, speed, kms travelled during the day, temperature, door status
*/